import { hasRole, isStatisticsSummaryOnly, type ProjectRole } from "src/permissions";

export type TEmailListKind = "shared" | "chargebacks";

export interface ISharedEmailsProfile {
	id: number;
	email?: string;
	projects: { id: number; roles: ProjectRole[] }[];
}

export interface ISharedEmailsFilters {
	from: string;
	to: string;
	currency?: string;
	client_id?: number;
	min_mids?: number;
	limit?: number;
	exclude?: string[];
}

export interface ISharedEmail {
	email: string;
	merchants: {
		id: number;
		name: string;
		clientId: number;
		clientName: string;
		attempts?: number;
		completed?: number;
		firstSeen?: string;
		lastSeen?: string;
	}[];
}

export interface ISharedEmailClient {
	id: number;
	name: string;
	merchants: ISharedEmail["merchants"];
}

export interface ISharedEmailsResult {
	rows: ISharedEmail[];
	total: number;
	truncated: boolean;
}

export interface IChargebackEmail {
	email: string;
	chargebacks?: number;
	clients?: {
		id: number;
		name: string;
		merchants: { id: number; name: string; chargebacks?: number; firstSeen?: string; lastSeen?: string }[];
	}[];
	charged?: number;
	returned?: number;
	midsCount?: number;
	firstSeen?: string;
	lastSeen?: string;
}

export interface IChargebackEmailsResult {
	rows: IChargebackEmail[];
	total: number;
	truncated: boolean;
	period: { from: string; to: string; days: number };
}

export const filterChargebackEmails = (rows: IChargebackEmail[], query: string): IChargebackEmail[] => {
	const terms = getSharedEmailSearchTerms(query);
	if (!terms.length) return rows;
	return rows.filter(row => {
		const text = [row.email, ...(row.clients ?? []).flatMap(client => [client.name, String(client.id),
			...client.merchants.flatMap(merchant => [merchant.name, String(merchant.id)])])].join(" ").toLowerCase();
		return terms.every(term => text.includes(term));
	});
};

export const getSharedEmailSearchTerms = (query: string) => [...new Set(query.trim().toLowerCase().split(/\s+/).filter(Boolean))];

export const filterSharedEmails = (rows: ISharedEmail[], query: string): ISharedEmail[] => {
	const terms = getSharedEmailSearchTerms(query);
	if (!terms.length) return rows;
	return rows.filter(row => {
		const text = [row.email, ...row.merchants.flatMap(merchant => [merchant.name, String(merchant.id), merchant.clientName, String(merchant.clientId)])].join(" ").toLowerCase();
		return terms.every(term => text.includes(term));
	});
};

export const groupSharedEmailClients = (row: ISharedEmail): ISharedEmailClient[] => {
	const clients = new Map<number, ISharedEmailClient>();
	for (const merchant of row.merchants) {
		let client = clients.get(merchant.clientId);
		if (!client) {
			client = { id: merchant.clientId, name: merchant.clientName, merchants: [] };
			clients.set(client.id, client);
		}
		client.merchants.push(merchant);
	}
	return [...clients.values()];
};

export const canViewSharedEmails = (profile: ISharedEmailsProfile | null | undefined, projectId: number) => {
	const email = profile?.email?.trim() ?? "";
	return /^[^@\s]+@[^@\s]+$/.test(email)
		&& !isStatisticsSummaryOnly(email)
		&& hasRole(profile?.projects.find(project => project.id === projectId)?.roles, "admin");
};

export const getSharedEmailsRequest = (filters: ISharedEmailsFilters, projectId: number) => ({
	method: "overview/emails/shared",
	httpMethod: "POST" as const,
	apiVersion: 2 as const,
	expectedProjectId: projectId,
	headers: { ignoreBusy: "true" },
	data: {
		from: filters.from,
		to: filters.to,
		...(filters.currency ? { currency: filters.currency } : {}),
		...(filters.client_id !== undefined ? { client_id: filters.client_id } : {}),
		...(filters.min_mids !== undefined ? { min_mids: filters.min_mids } : {}),
		...(filters.limit !== undefined ? { limit: filters.limit } : {}),
		...(filters.exclude !== undefined ? { exclude: filters.exclude } : {}),
	},
});

export const getChargebackEmailsRequest = (projectId: number) => ({
	method: "overview/emails/chargebacks",
	httpMethod: "POST" as const,
	apiVersion: 2 as const,
	expectedProjectId: projectId,
	headers: { ignoreBusy: "true" },
	data: {},
});

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const isId = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value > 0;
const parseCount = (value: unknown) => typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
const parseTimestamp = (value: unknown) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value)
	&& Number.isFinite(Date.parse(value)) && new Date(value).toISOString().replace(".000Z", "Z") === value.replace(".000Z", "Z") ? value : undefined;

export const parseChargebackEmails = (data: unknown): IChargebackEmailsResult | null => {
	if (!isRecord(data) || data.error) return null;
	const payload = isRecord(data.data) ? data.data : data;
	if (payload.error || !Array.isArray(payload.items) || typeof payload.total !== "number" || !Number.isSafeInteger(payload.total)
		|| payload.total < payload.items.length || !isRecord(payload.period)) return null;
	const { from, to, days } = payload.period;
	const isDate = (value: unknown): value is string => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
		&& Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
	if (!isDate(from) || !isDate(to) || from > to || !isId(days)) return null;
	const rows: IChargebackEmail[] = [];
	const emails = new Set<string>();
	for (const item of payload.items) {
		if (!isRecord(item) || !("chargebacks" in item) || typeof item.email !== "string" || !/^[^@\s]+@[^@\s]+$/.test(item.email.trim())) return null;
		const email = item.email.trim();
		if (emails.has(email.toLowerCase())) return null;
		emails.add(email.toLowerCase());
		const firstSeen = parseTimestamp(item.first_seen);
		const lastSeen = parseTimestamp(item.last_seen);
		const validOrder = !firstSeen || !lastSeen || Date.parse(firstSeen) <= Date.parse(lastSeen);
		let clients: IChargebackEmail["clients"];
		if (item.clients !== undefined) {
			if (!Array.isArray(item.clients)) return null;
			clients = [];
			const clientIds = new Set<number>();
			const merchantIds = new Set<number>();
			for (const client of item.clients) {
				if (!isRecord(client) || !isId(client.id) || typeof client.name !== "string" || clientIds.has(client.id)
					|| !Array.isArray(client.mids) || !client.mids.length) return null;
				clientIds.add(client.id);
				const merchants: NonNullable<IChargebackEmail["clients"]>[number]["merchants"] = [];
				for (const merchant of client.mids) {
					if (!isRecord(merchant) || !isId(merchant.id) || typeof merchant.name !== "string" || merchantIds.has(merchant.id)) return null;
					merchantIds.add(merchant.id);
					const merchantFirstSeen = parseTimestamp(merchant.first_seen);
					const merchantLastSeen = parseTimestamp(merchant.last_seen);
					const validMerchantOrder = !merchantFirstSeen || !merchantLastSeen || Date.parse(merchantFirstSeen) <= Date.parse(merchantLastSeen);
					merchants.push({ id: merchant.id, name: merchant.name.trim(), chargebacks: parseCount(merchant.chargebacks),
						firstSeen: validMerchantOrder ? merchantFirstSeen : undefined, lastSeen: validMerchantOrder ? merchantLastSeen : undefined });
				}
				clients.push({ id: client.id, name: client.name.trim(), merchants });
			}
		}
		rows.push({ email, chargebacks: parseCount(item.chargebacks), ...(clients ? { clients } : {}), charged: parseCount(item.charged), returned: parseCount(item.returned),
			midsCount: parseCount(item.mids_count), firstSeen: validOrder ? firstSeen : undefined, lastSeen: validOrder ? lastSeen : undefined });
	}
	return { rows, total: payload.total, truncated: payload.total > rows.length, period: { from, to, days } };
};

export const parseSharedEmails = (data: unknown): ISharedEmailsResult | null => {
	if (!isRecord(data) || data.error) return null;
	const payload = isRecord(data.data) ? data.data : data;
	if (!Array.isArray(payload.items) || typeof payload.total !== "number" || !Number.isSafeInteger(payload.total)
		|| payload.total < payload.items.length || typeof payload.truncated !== "boolean") return null;
	const rows: ISharedEmail[] = [];
	const emails = new Set<string>();
	for (const item of payload.items) {
		if (!isRecord(item) || typeof item.email !== "string" || !item.email.trim()
			|| !Array.isArray(item.clients) || !item.clients.length) return null;
		const email = item.email.trim();
		if (emails.has(email.toLowerCase())) return null;
		emails.add(email.toLowerCase());
		const merchants: ISharedEmail["merchants"] = [];
		const merchantIds = new Set<number>();
		for (const client of item.clients) {
			if (!isRecord(client) || !isId(client.id) || typeof client.name !== "string"
				|| !Array.isArray(client.mids) || !client.mids.length) return null;
			for (const merchant of client.mids) {
				if (!isRecord(merchant) || !isId(merchant.id) || typeof merchant.name !== "string" || merchantIds.has(merchant.id)) return null;
				merchantIds.add(merchant.id);
				const attempts = parseCount(merchant.attempts);
				const completed = parseCount(merchant.completed);
				const firstSeen = parseTimestamp(merchant.first_seen);
				const lastSeen = parseTimestamp(merchant.last_seen);
				const validOrder = !firstSeen || !lastSeen || Date.parse(firstSeen) <= Date.parse(lastSeen);
				merchants.push({ id: merchant.id, name: merchant.name.trim(), clientId: client.id, clientName: client.name.trim(),
					attempts, completed: attempts !== undefined && completed !== undefined && completed > attempts ? undefined : completed,
					firstSeen: validOrder ? firstSeen : undefined, lastSeen: validOrder ? lastSeen : undefined });
			}
		}
		rows.push({ email, merchants });
	}
	return { rows, total: payload.total, truncated: payload.truncated };
};